import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  AlertCircle,
  Backpack,
  Loader2,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from "@sportscanner/ui";
import { gearItemCreateSchema } from "@sportscanner/schemas";
import { apiDelete, apiGet, apiPost, apiPut, getApiErrorCode } from "@/lib/api";
import ClubScopeShell from "@/components/club-scope-shell";
import ConfirmDialog from "@/components/confirm-dialog";

interface GearSport {
  id: string;
  name: string;
}

interface GearItem {
  id: string;
  sportId: string;
  sportName: string;
  name: string;
  description: string | null;
  pricePerSession: string;
  quantity: number;
  isActive: boolean;
  displayOrder: number | null;
  imageUrl: string | null;
}

interface GearData {
  allowGearRental: boolean;
  sports: GearSport[];
  items: GearItem[];
}

export default function GearPage() {
  const { t } = useTranslation();
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="font-heading text-xl sm:text-2xl font-bold text-neutral-800 mb-1">
          {t("gear.title")}
        </h1>
        <p className="text-sm text-neutral-500">{t("gear.subtitle")}</p>
      </div>

      <ClubScopeShell>
        {(clubId) => <GearManager clubId={clubId} />}
      </ClubScopeShell>
    </div>
  );
}

function GearManager({ clubId }: { clubId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<GearItem | "new" | null>(null);
  const [deleting, setDeleting] = useState<GearItem | null>(null);

  const gearQuery = useQuery({
    queryKey: ["admin", "gear", clubId],
    queryFn: () => apiGet<GearData>(`/api/admin/clubs/${clubId}/gear`),
  });

  const toggleMutation = useMutation({
    mutationFn: (allowGearRental: boolean) =>
      apiPut(`/api/admin/clubs/${clubId}/gear/settings`, { allowGearRental }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["admin", "gear", clubId],
      });
    },
    onError: (err) => toast.error(t(`errors.api.${getApiErrorCode(err)}`)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiDelete(`/api/admin/clubs/${clubId}/gear/${id}`),
    onSuccess: async () => {
      toast.success(t("gear.deletedToast"));
      setDeleting(null);
      await queryClient.invalidateQueries({
        queryKey: ["admin", "gear", clubId],
      });
    },
    onError: (err) => toast.error(t(`errors.api.${getApiErrorCode(err)}`)),
  });

  if (gearQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }
  if (gearQuery.isError || !gearQuery.data) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{t("errors.api.INTERNAL_ERROR")}</AlertDescription>
      </Alert>
    );
  }

  const { allowGearRental, sports, items } = gearQuery.data;
  const sportLabel = (name: string) =>
    t(`sports.${name}`, { defaultValue: name });

  return (
    <div className="space-y-6">
      {/* Enable toggle */}
      <section className="bg-white rounded-xl border border-neutral-200 p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="text-sm font-medium text-neutral-800">
              {t("gear.enableLabel")}
            </Label>
            <p className="text-xs text-neutral-500 mt-0.5">
              {t("gear.enableHint")}
            </p>
          </div>
          <Switch
            checked={allowGearRental}
            disabled={toggleMutation.isPending}
            onCheckedChange={(v) => toggleMutation.mutate(v)}
          />
        </div>
        {allowGearRental &&
          items.length > 0 &&
          items.every((i) => !i.isActive) && (
            <p className="mt-3 text-xs text-amber-600">
              {t("gear.allInactiveHint")}
            </p>
          )}
      </section>

      {/* Catalogue */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-base font-semibold text-neutral-800">
          {t("gear.catalogueTitle")}
        </h2>
        <Button
          size="sm"
          onClick={() => setEditing("new")}
          disabled={sports.length === 0}
          className="bg-primary-600 hover:bg-primary-700 text-white"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          {t("gear.add")}
        </Button>
      </div>

      {sports.length === 0 ? (
        <div className="bg-white rounded-xl border border-neutral-200 p-10 text-center">
          <p className="text-sm text-neutral-500">{t("gear.noSports")}</p>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-neutral-200 p-10 text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto">
            <Backpack className="w-6 h-6 text-neutral-400" />
          </div>
          <p className="text-sm text-neutral-500">{t("gear.empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <GearCard
              key={item.id}
              item={item}
              sportLabel={sportLabel}
              onEdit={() => setEditing(item)}
              onDelete={() => setDeleting(item)}
            />
          ))}
        </div>
      )}

      {editing && (
        <GearEditDialog
          clubId={clubId}
          sports={sports}
          sportLabel={sportLabel}
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={t("gear.deleteTitle")}
        description={
          deleting ? t("gear.deleteBody", { name: deleting.name }) : undefined
        }
        confirmLabel={t("common.delete")}
        variant="destructive"
        isPending={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
      />
    </div>
  );
}

function GearCard({
  item,
  sportLabel,
  onEdit,
  onDelete,
}: {
  item: GearItem;
  sportLabel: (name: string) => string;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-4 flex items-start gap-4">
      {/* Placeholder thumbnail — real image upload comes later. */}
      <div className="w-14 h-14 shrink-0 rounded-lg bg-neutral-100 flex items-center justify-center overflow-hidden">
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <Backpack className="w-6 h-6 text-neutral-400" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium text-neutral-800 truncate">{item.name}</p>
          <Badge variant="secondary">{sportLabel(item.sportName)}</Badge>
          {!item.isActive && (
            <Badge variant="secondary">{t("gear.inactive")}</Badge>
          )}
        </div>
        {item.description && (
          <p className="text-sm text-neutral-500 mt-0.5 line-clamp-2">
            {item.description}
          </p>
        )}
        <p className="text-sm text-neutral-600 mt-1">
          {t("gear.priceEach", { price: item.pricePerSession })} ·{" "}
          {t("gear.unitsOwned", { qty: item.quantity })}
        </p>
      </div>
      <div className="flex shrink-0 gap-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={onEdit}
          aria-label={t("common.edit")}
        >
          <Pencil className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={onDelete}
          aria-label={t("common.delete")}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function GearEditDialog({
  clubId,
  sports,
  sportLabel,
  item,
  onClose,
}: {
  clubId: string;
  sports: GearSport[];
  sportLabel: (name: string) => string;
  item: GearItem | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [sportId, setSportId] = useState(item?.sportId ?? sports[0]?.id ?? "");
  const [name, setName] = useState(item?.name ?? "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [price, setPrice] = useState(item ? item.pricePerSession : "");
  const [quantity, setQuantity] = useState(item ? String(item.quantity) : "1");
  const [isActive, setIsActive] = useState(item?.isActive ?? true);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    setErrorCode(null);
  }, [item]);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        sportId,
        name: name.trim(),
        description: description.trim() || null,
        pricePerSession: Number(price || 0),
        quantity: Number(quantity || 0),
        isActive,
        displayOrder: item?.displayOrder ?? null,
      };
      const parsed = gearItemCreateSchema.safeParse(payload);
      if (!parsed.success) {
        throw { error: parsed.error.issues[0]?.message ?? "VALIDATION_FAILED" };
      }
      if (item) {
        await apiPut(`/api/admin/clubs/${clubId}/gear/${item.id}`, parsed.data);
      } else {
        await apiPost(`/api/admin/clubs/${clubId}/gear`, parsed.data);
      }
    },
    onSuccess: async () => {
      toast.success(t("common.saved"));
      await queryClient.invalidateQueries({
        queryKey: ["admin", "gear", clubId],
      });
      onClose();
    },
    onError: (err) => setErrorCode(getApiErrorCode(err)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {item ? t("gear.editTitle") : t("gear.addTitle")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {errorCode && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t(`errors.api.${errorCode}`)}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-1.5">
            <Label className="text-sm text-neutral-600">
              {t("gear.sportLabel")}
            </Label>
            <Select value={sportId} onValueChange={setSportId}>
              <SelectTrigger className="rounded-lg border-neutral-200 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sports.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {sportLabel(s.name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="gear-name" className="text-sm text-neutral-600">
              {t("gear.nameLabel")}
            </Label>
            <Input
              id="gear-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("gear.namePlaceholder")}
              className="rounded-lg border-neutral-200"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="gear-desc" className="text-sm text-neutral-600">
              {t("gear.descriptionLabel")}
            </Label>
            <Textarea
              id="gear-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder={t("gear.descriptionPlaceholder")}
              className="rounded-lg border-neutral-200"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="gear-price" className="text-sm text-neutral-600">
                {t("gear.priceLabel")}
              </Label>
              <Input
                id="gear-price"
                type="number"
                min={0}
                step="1"
                inputMode="numeric"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0"
                className="rounded-lg border-neutral-200"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="gear-qty" className="text-sm text-neutral-600">
                {t("gear.quantityLabel")}
              </Label>
              <Input
                id="gear-qty"
                type="number"
                min={1}
                step="1"
                inputMode="numeric"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="rounded-lg border-neutral-200"
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div>
              <Label className="text-sm font-medium text-neutral-800">
                {t("gear.activeLabel")}
              </Label>
              <p className="text-xs text-neutral-500 mt-0.5">
                {t("gear.activeHint")}
              </p>
            </div>
            <Switch checked={isActive} onCheckedChange={setIsActive} />
          </div>

          {/* Image upload is a planned follow-up — a placeholder is shown for now. */}
          <div className="flex items-center gap-3 rounded-lg border border-dashed border-neutral-200 p-3">
            <div className="w-12 h-12 shrink-0 rounded-lg bg-neutral-100 flex items-center justify-center">
              <Backpack className="w-5 h-5 text-neutral-400" />
            </div>
            <p className="text-xs text-neutral-500">
              {t("gear.imageComingSoon")}
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="bg-primary-600 hover:bg-primary-700 text-white"
            >
              {mutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("common.save")
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
